import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { API } from "@/siteConfig";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" });

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/blog/posts`)
      .then((res) => setPosts(res.data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <main data-testid="blog-page">
      <section className="relative overflow-hidden pt-40 pb-16 md:pt-48 md:pb-24">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 50% 40% at 75% 0%, rgba(43,217,199,0.07), transparent 70%)",
          }}
        />
        <div className="relative mx-auto max-w-7xl px-6 md:px-10">
          <Reveal>
            <p className="overline-label mb-8">Insights</p>
          </Reveal>
          <h1
            className="max-w-4xl font-serif text-4xl leading-[1.02] tracking-tighter text-white sm:text-5xl md:text-7xl"
            data-testid="blog-headline"
          >
            <MaskedLines
              lines={[
                "Notes from the",
                <span key="i" className="italic text-teal">Inflection Point</span>,
              ]}
            />
          </h1>
          <Reveal delay={0.3}>
            <p className="mt-8 max-w-xl font-sans text-base leading-relaxed text-white/60 md:text-lg">
              Perspective on leadership, transformation, and building companies that change well.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 md:px-10 md:pb-32">
        {loading ? (
          <p className="py-16 font-sans text-sm text-white/40" data-testid="blog-loading">Loading articles…</p>
        ) : posts.length === 0 ? (
          <p className="py-16 font-sans text-sm text-white/40" data-testid="blog-empty">
            Articles are on their way. Check back soon.
          </p>
        ) : (
          <div className="divide-y divide-white/10 border-y border-white/10" data-testid="blog-list">
            {posts.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.06} y={20}>
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-post-${p.slug}`}
                  className="group grid gap-4 py-10 md:grid-cols-12 md:gap-8"
                >
                  <span className="font-sans text-xs tracking-[0.3em] text-teal md:col-span-1">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="md:col-span-7">
                    <h2 className="font-serif text-2xl leading-snug text-white transition-transform duration-300 group-hover:translate-x-2 sm:text-3xl">
                      {p.title}
                    </h2>
                    <p className="mt-3 max-w-xl font-sans text-sm leading-relaxed text-white/50">
                      {p.excerpt}
                    </p>
                  </div>
                  <div className="flex items-center justify-between md:col-span-4 md:flex-col md:items-end md:justify-center md:gap-2">
                    <span className="font-sans text-xs uppercase tracking-[0.2em] text-white/40">
                      {p.category}
                    </span>
                    <span className="font-sans text-xs text-white/40">
                      {formatDate(p.date)} · {p.read_time}
                    </span>
                  </div>
                  <span className="hidden md:col-span-0" />
                  <ArrowUpRight className="h-5 w-5 text-white/30 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-teal md:hidden" />
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </section>

      <Marquee />
    </main>
  );
}
